import React, { useCallback, useEffect, useState } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import { ActionButton } from "../../components/ui/ActionButton";
import { Button } from "../../components/ui/Button";
import { Pagination } from "../../components/ui/Pagination";
import { QueueSpinner, StatusTabs, SuccessToast } from "../../components/ui/ResearchQueue";
import { SearchFilterForm } from "../../components/ui/SearchFilterForm";
import { Select } from "../../components/ui/Select";
import { useResearchQueue } from "../../hooks/useResearchQueue";
import { settingsApi, translationsApi } from "../../utils/api";
import { languageName, plural } from "../../utils/helpers";
import { KIND_LABELS, STATUS_LABELS, TranslationRow, isApprovable, isServed, rowId } from "./TranslationRow";
import { MAX_RETRANSLATE_ROWS } from "./reviewReasons";
import { translationRowActions } from "./rowActions";

// `needs_review`: a post-check rejected the machine's candidate, kept for the reviewer. `failed`: the
// provider failed and the next run selects the text again, so it is listed but never emailed. `all`
// is every row, the ones readers are served included (#839).
const TABS = [
  { value: "needs_review", label: "Needs review" },
  { value: "failed", label: "Failed" },
  { value: "all", label: "All" },
];

const PAGE_SIZE = 25;

const KIND_OPTIONS = [
  { value: "", label: "All fields" },
  ...Object.entries(KIND_LABELS).map(([value, label]) => ({ value, label })),
];
const STATUS_OPTIONS = [
  { value: "", label: "Any status" },
  ...Object.entries(STATUS_LABELS).map(([value, label]) => ({ value, label })),
];

// `useResearchQueue` reads `records` and needs a referentially stable `api`.
const queueApi = {
  getAll: async (params) => {
    const data = await translationsApi.getAll(params);
    return { records: data.translations, pagination: data.pagination };
  },
};

// What a bulk action did, in one line: what happened to how many rows.
const OUTCOME_WORDS = {
  approved: "approved",
  discarded: "discarded",
  retranslated: "retranslated; awaiting review",
  skipped: "skipped (not awaiting review, or no candidate to approve)",
  failed: "failed at the provider (left as they were, unless they had already failed)",
  changed: "changed by someone else meanwhile, so left as they were",
  not_found: "already gone",
};

export function outcomeMessage(counts, action) {
  return Object.entries(counts)
    .map(([outcome, count]) =>
      // A bulk retranslate skips only served rows, which it never sends (#839).
      action === "retranslate" && outcome === "skipped"
        ? `skipped ${count} served`
        : `${plural(count, "translation")} ${OUTCOME_WORDS[outcome] ?? outcome}`
    )
    .join(" · ");
}

// How many of `rows` an admin reviewed (an edit or an approval: `edited_by` is both), which a bulk
// discard deletes.
function reviewedNote(rows) {
  const reviewed = rows.filter((row) => row.edited_by).length;
  if (!reviewed) return "";
  return ` ${reviewed === 1 ? "1 reviewed translation is" : `${reviewed} reviewed translations are`} lost.`;
}

/**
 * Admin → Translations (#692, #839): the review queue — machine translations a post-check rejected
 * and those the provider failed on — and, under All, every row, searchable. Each row can be edited
 * or approved, given a suggested retranslation to review, or discarded; a selection can be approved,
 * retranslated for review or discarded. Runs on the research queues'
 * hook, so the stale-response guard, the page clamp and the silent re-read after an action are
 * theirs.
 */
export default function TranslationsPage() {
  const [locale, setLocale] = useState("");
  const [kind, setKind] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [summary, setSummary] = useState(null);
  // The locales readers are served (Settings → Localized content); null until read, or unreadable.
  const [servedLocales, setServedLocales] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const {
    records,
    pagination,
    status,
    page,
    setPage,
    loading,
    error,
    success,
    busy,
    load,
    act,
    resetPage,
    changeStatus,
    searchInput,
    setSearchInput,
    search,
    runSearch,
    resetSearch,
  } = useResearchQueue({
    api: queueApi,
    pageSize: PAGE_SIZE,
    entityLabel: "translation",
    // Under All, the status filter replaces the tab's `status` param (it is cleared on every tab change).
    filters: {
      ...(locale ? { locale } : {}),
      ...(kind ? { kind } : {}),
      ...(statusFilter ? { status: statusFilter } : {}),
    },
    initialStatus: "needs_review",
    loadErrorMessage: "Failed to load translations",
  });

  const loadSummary = useCallback(async () => {
    try {
      setSummary(await translationsApi.getSummary());
    } catch {
      // The tab counts are decoration; the list reports its own failure.
      setSummary(null);
    }
  }, []);

  useEffect(() => {
    loadSummary();
    settingsApi
      .getLocalizedContentSettings()
      .then((settings) => setServedLocales(settings.enabled_locales ?? []))
      .catch(() => setServedLocales(null));
  }, [loadSummary]);

  // A selection is of the rows on screen: another page or filter starts it over.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [page, status, locale, kind, statusFilter, search]);

  const selection = records.filter((row) => selectedIds.has(rowId(row)));
  const allSelected = records.length > 0 && selection.length === records.length;
  const toggleSelected = (row) =>
    setSelectedIds((current) => {
      const next = new Set(current);
      if (!next.delete(rowId(row))) next.add(rowId(row));
      return next;
    });
  const toggleAll = () => setSelectedIds(allSelected ? new Set() : new Set(records.map(rowId)));

  // Every action re-reads the page rather than splicing the row out: pagination is by offset, so the
  // row moving up into this page would otherwise never be shown.
  const run = (key, action, message) =>
    act(
      key,
      async () => {
        try {
          return await action();
        } finally {
          loadSummary();
        }
      },
      message
    );

  // A Retranslate writes nothing (#839), so the page is not re-read after it. The example names the
  // event to build the prompt's context from, so the API need not look it up.
  const { save, approve, retranslate, discard } = translationRowActions({
    run,
    suggest: (key, action) => act(key, action, undefined, { readBack: false }),
    contextEventId: (row) => (row.example?.type === "event" ? row.example.id : undefined),
  });

  // A bulk action reports each outcome's count and clears the selection it acted on.
  const bulk = (action, prepare) =>
    run(`bulk:${action}`, async () => {
      if (prepare && !(await prepare())) return undefined;
      const { counts } = await translationsApi.bulk(action, selection);
      setSelectedIds(new Set());
      return outcomeMessage(counts, action);
    });

  const bulkDiscard = () => {
    const message = `Discard ${plural(selection.length, "translation")}? Readers see the source text until the next nightly run translates them again.${reviewedNote(selection)}`;
    if (window.confirm(message)) bulk("discard");
  };

  // The estimate is its own request, before the confirmation that quotes it; nothing is sent until
  // the admin agrees.
  const bulkRetranslate = () =>
    bulk("retranslate", async () => {
      const estimate = await translationsApi.bulk("retranslate", selection, { estimateOnly: true });
      const cost = `$${Number(estimate.estimated_cost_usd ?? 0).toFixed(4)}`;
      // The server's count, which a stale page cannot contradict.
      const served = estimate.counts?.skipped ?? 0;
      const skipped = served ? ` ${plural(served, "served row")} will be skipped.` : "";
      return window.confirm(`Retranslate ${plural(estimate.translations, "translation")} for about ${cost}?${skipped}`);
    });

  const counts = summary?.[status] ?? {};
  // Every target, so All can be narrowed to a language with nothing to review (#839); plus any locale
  // a review row still names after it stopped being one.
  const locales = [
    ...new Set([...(summary?.target_locales ?? []), ...Object.keys({ ...summary?.needs_review, ...summary?.failed })]),
  ].sort();
  const localeOptions = [
    { value: "", label: "All languages" },
    ...locales.map((code) => ({
      value: code,
      label: status === "all" ? languageName(code) : `${languageName(code)} (${counts[code] ?? 0})`,
    })),
  ];
  const tabs = TABS.map((tab) => ({
    ...tab,
    label:
      summary && tab.value !== "all"
        ? `${tab.label} (${(summary[`${tab.value}_total`] ?? 0).toLocaleString()})`
        : tab.label,
  }));
  const approvable = selection.filter(isApprovable).length;
  const retranslateBlocked =
    selection.length > MAX_RETRANSLATE_ROWS
      ? `At most ${MAX_RETRANSLATE_ROWS} at once`
      : selection.every(isServed)
        ? "Every selected translation is served: Retranslate a single row to get a suggestion"
        : undefined;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Translations</h1>
          <p className="text-sm text-muted-foreground">
            Machine translations that need a person — the ones a check rejected and the ones that failed — and,
            under All, every translation readers are served.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => {
            load();
            loadSummary();
          }}
          disabled={loading}
        >
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
          Refresh
        </Button>
      </div>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          <StatusTabs
            status={status}
            onChange={(value) => {
              // The search and status filter live under All alone: carried to another tab they would
              // cut its list with no control on screen to undo them.
              setStatusFilter("");
              resetSearch();
              changeStatus(value);
            }}
            tabs={tabs}
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {status === "all" && (
            <SearchFilterForm
              value={searchInput}
              onChange={setSearchInput}
              onSubmit={runSearch}
              onClear={search ? resetSearch : undefined}
              placeholder="Search source or translation"
            />
          )}
          {status === "all" && (
            <div className="w-48">
              <Select
                aria-label="Status"
                value={statusFilter}
                options={STATUS_OPTIONS}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  resetPage();
                }}
              />
            </div>
          )}
          <div className="w-44">
            <Select
              aria-label="Field"
              value={kind}
              options={KIND_OPTIONS}
              onChange={(e) => {
                setKind(e.target.value);
                resetPage();
              }}
            />
          </div>
          <div className="w-52">
            <Select
              aria-label="Language"
              value={locale}
              options={localeOptions}
              onChange={(e) => {
                setLocale(e.target.value);
                resetPage();
              }}
            />
          </div>
        </div>
      </div>

      {status === "failed" && (
        <p className="text-sm text-muted-foreground">
          The next translation run tries these again by itself. Retranslate or write one only when it keeps failing.
        </p>
      )}

      {error && (
        <div className="rounded-md border border-red-500/20 bg-red-500/10 p-3 text-sm text-red-700">{error}</div>
      )}
      <SuccessToast message={success} />

      {!loading && records.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-md border bg-muted/30 px-3 py-2 text-sm">
          <label className="flex items-center gap-2">
            <input type="checkbox" className="h-4 w-4" checked={allSelected} onChange={toggleAll} />
            {selection.length ? `${selection.length} selected` : "Select all on this page"}
          </label>
          {selection.length > 0 && (
            <div className="flex flex-wrap gap-2">
              <ActionButton busy={busy} actionKey="bulk:approve" disabled={!approvable} onClick={() => bulk("approve")}>
                Approve selected{approvable !== selection.length ? ` (${approvable})` : ""}
              </ActionButton>
              <ActionButton
                busy={busy}
                actionKey="bulk:retranslate"
                disabled={Boolean(retranslateBlocked)}
                title={retranslateBlocked}
                onClick={bulkRetranslate}
              >
                Retranslate selected
              </ActionButton>
              <ActionButton busy={busy} actionKey="bulk:discard" onClick={bulkDiscard}>
                Discard selected
              </ActionButton>
            </div>
          )}
        </div>
      )}

      {loading ? (
        <QueueSpinner />
      ) : !records.length ? (
        <p className="py-10 text-center text-sm text-muted-foreground">
          {status === "all" ? "No translations match." : "Nothing to review."}
        </p>
      ) : (
        <div className="space-y-3">
          {records.map((row) => (
            <TranslationRow
              key={`${rowId(row)}:${row.status}:${row.updated_at}`}
              row={row}
              busy={busy}
              selected={selectedIds.has(rowId(row))}
              onSelect={toggleSelected}
              served={servedLocales ? servedLocales.includes(row.locale) : null}
              showStatus={status === "all"}
              onSave={save}
              onApprove={approve}
              onRetranslate={retranslate}
              onDiscard={discard}
            />
          ))}
        </div>
      )}

      <Pagination page={page} totalPages={pagination.total_pages} onPageChange={setPage} />
    </div>
  );
}
